import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { getFeatureFlags, getExposureFlags, type PlanTier } from '@/lib/features/featureFlags';
import { useFeatureStore } from '@/stores/featureStore';
import { applyStoredOverrides } from '@/hooks/useFeatureFlagOverrides';
import { toast } from '@/components/ui/use-toast';
// ARC-A4: switched from `useIsInternalAdmin()` (which calls useAuth()
// internally) to `useIsInternalAdminForUserId(userId)` because
// AuthProvider's body runs ABOVE its own `<AuthContext.Provider
// value={...}>` JSX. Calling useAuth() from a component rendered
// above its own Provider throws ("useAuth must be used within an
// AuthProvider"). The userId-arg variant accepts the user.id directly
// from AuthProvider's local `user` state, avoiding the recursive
// dependency. Pre-A4 the public hook wrapped a duplicate
// `onAuthStateChange` listener inside the same hook body — that
// duplicate listener is what ARC-A4 eliminates here. Behavior preserved:
// same queryKey, same RPC, same staleTime; only the user.id source
// moves from a duplicate listener to AuthContext's own state.
import { useIsInternalAdminForUserId } from '@/hooks/useIsInternalAdmin';
// DTC-J: route auth-state `created_at` audit-trail writes through the
// canonical clock so persisted auth-event timestamps are deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';
import { debugLog } from '@/utils/debugLog';

type Shop = Database['public']['Tables']['shops']['Row'];
type Membership = Database['public']['Tables']['memberships']['Row'];
type AppRole = Database['public']['Enums']['app_role'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  activeShop: Shop | null;
  membership: Membership | null;
  userShops: Shop[];
  isLoading: boolean;
  isShopDataLoading: boolean;
  /** OCT20A: false while the active membership's work roles are still being read (work-role routes wait for it). */
  workRolesResolved?: boolean;
  isPassportMode: boolean;
  hasScopeAccess: boolean;
  // FIX-R38-CRIT-1: expose for routing (e.g., ProtectedRoute uses this to
  // prefer /school over /passport for school-only users).
  hasSchoolMembership: boolean;
  setActiveShop: (shopId: string) => Promise<void>;
  signOut: () => Promise<void>;
  refetchMembership: () => Promise<void>;
  /**
   * Force a full reload of userShops + activeShop + membership +
   * schoolMembershipCount by re-running `loadUserShops`. Use this
   * after any flow that creates a NEW shop membership server-side
   * for the current user — e.g. `provision-beta-org` completing
   * (beta invite onboarding) or an admin grant — when the local
   * AuthContext snapshot was taken before that row existed.
   *
   * Difference from `refetchMembership`:
   *   - `refetchMembership` is gated on `user && activeShop` and
   *     only refreshes the single membership row for the currently
   *     active shop. It does NOT update userShops, so it cannot
   *     transition a user from "no shop" to "has shop" — that's
   *     `refetchUserShops`'s job.
   *
   * This also resets `lastLoadedUserIdRef.current` so the
   * same-user dedup in `loadShopsOnce` (used by the
   * onAuthStateChange listener) doesn't block subsequent token-
   * refresh-driven reloads that may be triggered after this call.
   *
   * The canonical refresh path for membership changes during a live
   * session is the `auth-memberships-${user.id}` postgres_changes
   * channel (AuthContext L696-723). `refetchUserShops` is the
   * explicit fallback the beta-invite flow needs — it doesn't depend
   * on the channel actually delivering events (which fails today
   * because `memberships` was missing from the supabase_realtime
   * publication; see the membership-publication migration that
   * ships alongside this method).
   */
  refetchUserShops: () => Promise<void>;
  refreshActiveShop: () => Promise<void>;
}

// P5-A2: exported so tests can wrap rendered hooks/components in a
// `<AuthContext.Provider value={mockValue}>` via the shared
// `tests/setup/hookHarness.tsx` helper. The runtime public API
// remains `useAuth()` (which throws when no Provider is present —
// see L713 below); production code paths MUST keep using `useAuth()`
// rather than `useContext(AuthContext)` directly so the throw-guard
// keeps detecting accidental "rendered outside AuthProvider"
// regressions. Adding `export` is a 1-character touch with zero
// behavior change for existing call sites.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);


const mapShopTierToPlanTier = (tier: string | null): PlanTier => {
  const normalized = (tier || 'CORE').toUpperCase();

  if (normalized === 'CATALYST' || normalized === 'ENTERPRISE') {
    return normalized as PlanTier;
  }

  // Backward compat: legacy 'DISPATCH' maps to 'CATALYST'
  if (normalized === 'DISPATCH') return 'CATALYST';

  // Default / unknown values fall back to CORE
  return 'CORE';
};

const updateFeatureFlagsForShop = (shop: (Shop & { addons?: string[] }) | null) => {
  const { setFeatureFlags, setExposureFlags } = useFeatureStore.getState();

  // Dev-gated via debugLog: this runs on every auth-state pass (initial
  // session restore, sign-in, shop activation, sign-out), so in production
  // it printed ~10 lines of tier/addon detail into the customer's console
  // on every page load. Kept at full fidelity in dev, where it is the
  // primary trace for flag-computation bugs.
  debugLog('FEATURE-FLAGS', 'updateFeatureFlagsForShop called with:', {
    shopName: shop?.name,
    shopId: shop?.id,
    tier: shop?.tier,
    addons: (shop as any)?.addons,
  });

  if (!shop) {
    // When no shop is active, default to CORE-level baseline
    debugLog('FEATURE-FLAGS', 'No shop, setting CORE baseline');
    setFeatureFlags(getFeatureFlags('CORE', []));
    setExposureFlags(getExposureFlags('CORE', []));
    return;
  }

  const plan = mapShopTierToPlanTier(shop.tier || 'CORE');

  // Load addons from shop table (e.g., ['OPTIQUEUE'])
  const addons: string[] = (shop as any).addons || [];

  const flags = getFeatureFlags(plan, addons);
  debugLog('FEATURE-FLAGS', 'Computed flags:', { plan, addons, optiQueue: flags.optiQueue });
  setFeatureFlags(flags);
  setExposureFlags(getExposureFlags(plan, addons));
  
  // Layer any sessionStorage-persisted admin overrides on top
  applyStoredOverrides();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [activeShop, setActiveShopState] = useState<Shop | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);

  // ── MULTI-ROLE: the work-role set travels WITH the membership ────────────
  //
  // `authority_role` arrives on the membership row itself (the selects are
  // `*`), but work roles live in a child table, so they have to be fetched and
  // attached. Every consumer — usePermissions, assignment eligibility, Team
  // Management — resolves through the single membership object, so loading
  // them here means no call site has to remember to.
  //
  // NULL vs [] is load-bearing downstream: `null` means "not loaded yet" and
  // the resolver falls back to the legacy scalar, while `[]` means "loaded,
  // performs no operational work". So this stays null until a fetch actually
  // completes, and a FAILED fetch leaves it null rather than asserting the
  // person does nothing.
  const [workRoles, setWorkRoles] = useState<string[] | null>(null);
  // OCT20A WS-BROWSER2: which membership's work-role read has SETTLED (answered or failed). A route admitted only by
  // a work role (e.g. `/` for a Detail-Specialist-only member) must not decide while the read is still in flight —
  // `null` then means "not loaded yet", and a refusal with `forbiddenRedirectTo` is a permanent redirect.
  const [workRolesReadFor, setWorkRolesReadFor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const membershipId = membership?.id;
    if (!membershipId) { setWorkRoles(null); return; }

    (async () => {
      try {
        const { data, error } = await supabase
          .from('membership_work_roles' as never)
          .select('work_role')
          .eq('membership_id', membershipId);
        if (cancelled) return;
        if (error) { setWorkRoles(null); setWorkRolesReadFor(membershipId); return; }
        setWorkRoles(((data ?? []) as { work_role: string }[]).map((r) => r.work_role));
        setWorkRolesReadFor(membershipId);
      } catch {
        if (!cancelled) { setWorkRoles(null); setWorkRolesReadFor(membershipId); }
      }
    })();

    return () => { cancelled = true; };
  }, [membership?.id]);
  const workRolesResolved = !membership?.id || workRolesReadFor === membership.id;

  /** The membership as the rest of the app should see it: both halves. */
  const membershipWithRoles = useMemo(
    () => (membership ? ({ ...membership, work_roles: workRoles } as Membership) : null),
    [membership, workRoles],
  );
  const [userShops, setUserShops] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isShopDataLoading, setIsShopDataLoading] = useState(false);
  const [hasScopeAccess, setHasScopeAccess] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  // FIX-R38-CRIT-1: track school membership count alongside shop memberships
  // so passport-mode routing doesn't bounce school-only users to /passport.
  // We only need a count for routing decisions; the full school context is
  // resolved by useMySchoolMembership in the school subtree.
  const [schoolMembershipCount, setSchoolMembershipCount] = useState(0);

  // FIX-R32-M-1: guard against double `loadUserShops` when the
  // onAuthStateChange listener fires for the initial session AND the
  // trailing `getSession()` promise resolves at about the same time.
  // We key on the user id of the most recent load so the second
  // firing for the same id is a no-op, while a subsequent sign-in as
  // a different user still triggers a fresh load.
  //
  // Hoisted to the top of the component (was further down, inside the
  // initialize-auth-state block) so `refetchUserShops` defined above
  // the auth-state useEffect can reset it. JS const TDZ would have
  // tripped TypeScript otherwise.
  const lastLoadedUserIdRef = useRef<string | null>(null);

  // ARC-A4: pass user.id directly to the userId-arg variant so this
  // hook does not spawn a duplicate `supabase.auth.onAuthStateChange`
  // listener that would otherwise fire alongside AuthContext's
  // canonical listener on every auth event (forming a render
  // feedback loop with this very component).
  const { data: isInternalAdmin } = useIsInternalAdminForUserId(user?.id ?? null);
  // FIX-R130-CRIT-1: react-query cache survives signOut and many query keys
  // are not user-id-scoped (is-internal-admin was the worst offender; see
  // useIsInternalAdmin). Clear the entire query cache on every sign-out so
  // no cached server data from the previous user can answer the next user's
  // queries before refetch.
  const queryClient = useQueryClient();

  // Internal admin override: enable all training UI exposure flags.
  // FIX-R130-MED-1: when isInternalAdmin transitions from true → false
  // (most importantly across a signOut/signIn boundary where the next
  // user is NOT an admin), explicitly recompute the exposure flags from
  // the current shop tier instead of leaving the previous user's admin
  // overrides in place. Without this, any window between "new user
  // session loaded" and "shop loaded" could render admin chrome to a
  // non-admin if the queryClient cache had any residual hit (the C1 fix
  // closes the cache leak; this is the second line of defense at the
  // consumer of that cache).
  useEffect(() => {
    if (isInternalAdmin) {
      const { exposureFlags, setExposureFlags } = useFeatureStore.getState();
      if (exposureFlags) {
        setExposureFlags({
          ...exposureFlags,
          ui_trainingCatalogAdmin: true,
          ui_trainingProviderAdmin: true,
          ui_trainingDiscoveryAdmin: true,
          ui_trainingLearnedMappings: true,
          ui_advancedAdminTools: true,
          ui_ecosystemAdmin: true,
        });
      }
    } else {
      // Explicitly recompute exposure flags from the current shop's tier
      // so any previously-set admin overrides are removed for non-admins.
      updateFeatureFlagsForShop(activeShop);
    }
  }, [isInternalAdmin, activeShop]);

  // Passport mode: authenticated but no active SHOP or SCHOOL memberships.
  // FIX-R38-CRIT-1: school-only users (school_admin / instructor with no
  // shop) must NOT be treated as passport-mode — that would bounce them to
  // /passport at boot instead of letting them reach /school. The
  // schoolMembershipCount is loaded inside loadUserShops() below.
  const isPassportMode =
    !isLoading && !isShopDataLoading && !!user
    && userShops.length === 0
    && schoolMembershipCount === 0;
  const hasSchoolMembership = schoolMembershipCount > 0;

  // ARC-A2: memoize the three loader helpers so the public callbacks
  // (setActiveShop, refetchMembership) can include them in deps without
  // invalidating on every render. Each loader is "pure-of-arguments":
  // reads only its parameters + module-level constants (supabase,
  // localStorage, updateFeatureFlagsForShop) + setStates (stable from
  // useState). No closure over component state, so the empty/minimal
  // dep arrays are safe.
  //
  // Declaration order: callees before callers (loadMembership →
  // loadMembershipWithScope → loadUserShops) so each useCallback's deps
  // array can name the predecessor without hitting a TDZ error.

  // Load membership for active shop (direct row lookup).
  const loadMembership = useCallback(async (userId: string, shopId: string) => {
    try {
      const { data, error } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', userId)
        .eq('shop_id', shopId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        console.error('Error loading membership:', error);
      }

      setMembership(data);
    } catch (error) {
      console.error('Failed to load membership:', error);
      setMembership(null);
    }
  }, []);

  // Load membership with scope fallback — tries direct first, then
  // derives via RPC.
  const loadMembershipWithScope = useCallback(async (userId: string, shopId: string, directShopIds: string[]) => {
    if (directShopIds.includes(shopId)) {
      // Direct membership — standard path
      await loadMembership(userId, shopId);
      return;
    }

    // Scope-derived: no direct membership row — derive role from RPC
    try {
      const { data: roleData, error: roleErr } = await supabase
        .rpc('get_user_shop_role', { _user_id: userId, _shop_id: shopId });

      if (roleErr || !roleData) {
        console.warn('[AUTH] Could not derive scope role for shop:', shopId);
        setMembership(null);
        return;
      }

      // Create a synthetic membership stub (never written to DB)
      setMembership({
        id: `derived-${shopId}`,
        user_id: userId,
        shop_id: shopId,
        role: roleData as AppRole,
        status: 'active',
        created_at: clock.now().toISOString(),
        organization_id: null,
        scope_type: 'organization',
        scope_id: null,
      } as Membership);
    } catch (e) {
      console.warn('[AUTH] Scope role derivation failed:', e);
      setMembership(null);
    }
  }, [loadMembership]);

  // Load user's shops and memberships (scope-aware).
  // FIX-R38-CRIT-1: also fetch a count of school memberships so the
  // routing layer can distinguish "no memberships at all" (passport-only)
  // from "school-only" (route to /school instead of /passport).
  const loadUserShops = useCallback(async (userId: string) => {
    setIsShopDataLoading(true);
    // Defense-in-depth: a thrown query (RLS / network / serialization) used
    // to strand `isShopDataLoading=true` because the three explicit
    // setIsShopDataLoading(false) calls below were not in a finally block.
    // Wrapping the body guarantees the flag is reset on every exit path.
    // Audit: docs/audits/stuck-loading-after-tab-return-audit.md.
    try {
      // School membership count + shop memberships fetched IN PARALLEL via
      // Promise.all so we don't serialize the round-trips, but we DO await
      // both before continuing.
      // FIX-R44-MED-1: previously the school count was fire-and-forget; if
      // the request failed silently, schoolMembershipCount stayed 0 and a
      // school-only user would be misclassified as passport-mode and routed
      // to /passport instead of /school. Awaiting and still treating
      // failure as non-fatal (default-zero) ensures the routing decision is
      // made on a known state, not a TOCTOU race.
      const [schoolCountResult, msResult] = await Promise.all([
        supabase
          .from('school_memberships')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('memberships')
          .select('shop_id')
          .eq('user_id', userId)
          .eq('status', 'active')
          .order('created_at', { ascending: true }),
      ]);

      if (schoolCountResult.error) {
        console.warn('[AUTH] school_memberships count failed (non-fatal):', schoolCountResult.error);
        setSchoolMembershipCount(0);
      } else {
        setSchoolMembershipCount(schoolCountResult.count || 0);
      }

      const { data: ms, error: mErr } = msResult;

      if (mErr) {
        console.error('Error loading memberships:', mErr);
        setUserShops([]);
        setHasScopeAccess(false);
        return;
      }

      const directShopIds = (ms || []).map((m: any) => m.shop_id).filter(Boolean);

      // Step 2: get scope-expanded shop IDs via RPC
      let scopeShopIds: string[] = [];
      try {
        const { data: rpcData, error: rpcErr } = await supabase
          .rpc('get_accessible_shop_ids', { _user_id: userId });
        if (!rpcErr && rpcData) {
          scopeShopIds = rpcData as string[];
        }
      } catch (e) {
        // RPC may not exist yet in some environments — gracefully degrade
        console.warn('[AUTH] get_accessible_shop_ids RPC unavailable, using direct memberships only');
      }

      // Step 3: merge (union) direct + scope shop IDs
      const allShopIds = [...new Set([...directShopIds, ...scopeShopIds])];
      const hasScopeExpansion = scopeShopIds.length > directShopIds.length;
      setHasScopeAccess(hasScopeExpansion);

      if (allShopIds.length === 0) {
        // No active memberships and no scope access
        setUserShops([]);
        setMembership(null);
        localStorage.removeItem('activeShopId');
        return;
      }

      // Step 4: fetch shops by ids with DETERMINISTIC ordering
      const { data: shopsData, error: shopsErr } = await supabase
        .from('shops')
        .select('*')
        .in('id', allShopIds)
        .order('name', { ascending: true });

      if (shopsErr) {
        console.error('Error loading shops:', shopsErr);
      }

      const shops = (shopsData || []) as Shop[];
      console.log('[AUTH] Loaded shops with addons:', shops.map(s => ({ id: s.id, name: s.name, addons: (s as any).addons })));
      setUserShops(shops);

      // Decide which shop to activate (fall back to the first id even if shop rows weren't returned)
      const storedShopId = localStorage.getItem('activeShopId');
      const selectedShopId = storedShopId && allShopIds.includes(storedShopId) ? storedShopId : (shops[0]?.id || allShopIds[0]);

      // Set active shop state if we have the full shop row
      const shopToActivate = shops.find(s => s.id === selectedShopId) || null;
      console.log('[AUTH] Shop to activate:', { id: shopToActivate?.id, name: shopToActivate?.name, addons: (shopToActivate as any)?.addons });
      if (shopToActivate) {
        setActiveShopState(shopToActivate);
        localStorage.setItem('activeShopId', shopToActivate.id);
        updateFeatureFlagsForShop(shopToActivate);
      }

      // Load membership — may be direct or derived
      await loadMembershipWithScope(userId, selectedShopId, directShopIds);
    } finally {
      setIsShopDataLoading(false);
    }
  }, [loadMembershipWithScope]);

  // Set active shop with server-side membership re-validation (M1).
  // ARC-A2: useCallback'd with [user, userShops, loadUserShops]. The
  // closure captures `user.id` + `userShops` (for the .find lookup) +
  // `loadUserShops` (for the access-denied recovery path).
  const setActiveShop = useCallback(async (shopId: string) => {
    if (!user) return;

    // M1: Server-side membership validation — try direct first
    const { data: membershipCheck, error } = await supabase
      .from('memberships')
      .select('*')
      .eq('user_id', user.id)
      .eq('shop_id', shopId)
      .eq('status', 'active')
      .maybeSingle();

    if (membershipCheck) {
      // Direct membership found — standard path
      const shop = userShops.find(s => s.id === shopId);
      if (shop) {
        setActiveShopState(shop);
        localStorage.setItem('activeShopId', shopId);
        updateFeatureFlagsForShop(shop);
        setMembership(membershipCheck as Membership);
      }
      return;
    }

    // No direct membership — try scope-based access via RPC
    try {
      const { data: canAccess } = await supabase
        .rpc('can_access_shop', { _user_id: user.id, _shop_id: shopId });

      if (canAccess) {
        const shop = userShops.find(s => s.id === shopId);
        if (shop) {
          setActiveShopState(shop);
          localStorage.setItem('activeShopId', shopId);
          updateFeatureFlagsForShop(shop);

          // Derive role from scope
          const { data: roleData } = await supabase
            .rpc('get_user_shop_role', { _user_id: user.id, _shop_id: shopId });

          setMembership({
            id: `derived-${shopId}`,
            user_id: user.id,
            shop_id: shopId,
            role: (roleData || 'OWNER') as AppRole,
            status: 'active',
            created_at: clock.now().toISOString(),
            organization_id: null,
            scope_type: 'organization',
            scope_id: null,
          } as Membership);
        }
        return;
      }
    } catch (e) {
      console.warn('[AUTH] Scope access check failed:', e);
    }

    // Neither direct nor scope access
    console.error('[AUTH] Membership validation failed for shop switch:', error);
    toast({
      title: 'Access Denied',
      description: 'You no longer have access to this shop.',
      variant: 'destructive'
    });
    localStorage.removeItem('activeShopId');
    await loadUserShops(user.id);
  }, [user, userShops, loadUserShops]);

  // Refetch membership (useful after role changes).
  // ARC-A2: useCallback'd with [user, activeShop, loadMembership]. Stable
  // across renders that don't change user / activeShop / loadMembership.
  const refetchMembership = useCallback(async () => {
    if (user && activeShop) {
      await loadMembership(user.id, activeShop.id);
    }
  }, [user, activeShop, loadMembership]);

  // Full userShops + activeShop + membership + schoolMembershipCount
  // reload via the canonical `loadUserShops` path. See the
  // `refetchUserShops` field comment on AuthContextType for the
  // contract — most importantly: this is the only refresh that can
  // transition a user from "no shop" → "has shop" without a page
  // reload, which is the exact transition the beta-invite onboarding
  // flow needs after `provision-beta-org` succeeds.
  const refetchUserShops = useCallback(async () => {
    if (!user) return;
    // Reset the same-user dedup so future onAuthStateChange firings
    // (token refresh, USER_UPDATED) can also trigger a re-load if
    // they need to. `loadUserShops` itself doesn't consult this ref —
    // it's only checked by the `loadShopsOnce` wrapper in the auth
    // listener (L524-528) — so this reset is forward-looking
    // defense, not strictly required to make this call work.
    lastLoadedUserIdRef.current = null;
    await loadUserShops(user.id);
  }, [user, loadUserShops]);

  // Refresh active shop data from DB (useful after addon toggles).
  // ARC-A2: useCallback'd with [activeShop]. Closure reads activeShop.id;
  // dep on full activeShop matches exhaustive-deps and re-creates the
  // callback whenever activeShop reference changes (which is exactly
  // when refresh-target semantics shift).
  const refreshActiveShop = useCallback(async () => {
    if (!activeShop?.id) return;
    const { data, error } = await supabase
      .from('shops')
      .select('*')
      .eq('id', activeShop.id)
      .single();
    if (!error && data) {
      const shop = data as Shop;
      setActiveShopState(shop);
      setUserShops(prev => prev.map(s => s.id === shop.id ? shop : s));
      updateFeatureFlagsForShop(shop);
    }
  }, [activeShop]);

  // Sign out.
  // ARC-A2: useCallback'd with [queryClient]. The closure reads only
  // setStates (stable from useState), supabase (module-level), localStorage
  // (global), updateFeatureFlagsForShop (top-level helper, stable), and
  // queryClient (from useQueryClient — stable but listed for exhaustive-
  // deps compliance). NO dependency on `user` / `activeShop` / `userShops`
  // — signOut works the same regardless of current state, so the callback
  // identity stays stable across the entire signed-in session.
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setActiveShopState(null);
    setMembership(null);
    setUserShops([]);
    setIsShopDataLoading(false);
    // FIX-R38-CRIT-1: also reset school membership count on signOut.
    setSchoolMembershipCount(0);
    // FIX-R32-H-1: also clear chronicle context + join-team token + tutorial
    // flag so none of the previous user's ancillary identifiers bleed into
    // the next sign-in.
    // FIX-R130-HIGH-1: also clear beta_invite_data — it holds an invite
    // token + email that BetaComplete consumes; if user A abandons mid-flow
    // and user B signs in, BetaComplete would otherwise provision A's
    // shop attached to B.
    try {
      localStorage.removeItem('activeShopId');
      localStorage.removeItem('chronicle_selected_context');
      localStorage.removeItem('joinTeamToken');
      localStorage.removeItem('startTutorialOnLoad');
      localStorage.removeItem('beta_invite_data');
    } catch {
      // localStorage may be unavailable in some embedded contexts; ignore.
    }

    // FIX-R130-CRIT-1: drop all cached query data so the next user's
    // first render cannot resolve a query against the previous user's
    // cached payload (most dangerous example: useIsInternalAdmin pre-fix,
    // but applies to any non-user-scoped key as well).
    try {
      queryClient.clear();
    } catch (err) {
      console.warn('[AUTH] queryClient.clear() failed (non-fatal):', err);
    }

    // Reset to a safe default when no user/shop is active
    updateFeatureFlagsForShop(null);
  }, [queryClient]);

  // Initialize auth state.
  // The `lastLoadedUserIdRef` declaration was hoisted to the top of the
  // component (see the dedup-guard comment near the state declarations)
  // so `refetchUserShops` can reset it from above this useEffect.
  useEffect(() => {
    const loadShopsOnce = (userId: string) => {
      if (lastLoadedUserIdRef.current === userId) return;
      lastLoadedUserIdRef.current = userId;
      loadUserShops(userId);
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Realtime channels carry the access token in the websocket
        // subprotocol at subscribe time and do NOT auto-rotate when
        // gotrue refreshes the token. Without this push, active
        // channels keep using the original token until it expires
        // server-side, at which point they surface as CHANNEL_ERROR
        // (see useLiveReshuffle's "Reconnecting" toast). Setting the
        // realtime auth on every auth event — including TOKEN_REFRESHED
        // and SIGNED_OUT (null token closes server-side auth) — keeps
        // live channels in sync with gotrue's current session.
        try {
          supabase.realtime.setAuth(session?.access_token ?? null);
        } catch (e) {
          console.warn('[AUTH] realtime.setAuth failed (non-fatal):', e);
        }

        if (session?.user) {
          // FIX-R130-MED-2: defend against silent user-swaps via
          // supabase.auth.setSession() that don't fire SIGNED_OUT in
          // between. If the new user.id differs from the last loaded
          // user, scrub the per-user query cache + identity-linked
          // localStorage before loading the new user's shops. Without
          // this, a (rare but possible) setSession swap would keep
          // user A's queries cached for user B until staleTime.
          const previousUserId = lastLoadedUserIdRef.current;
          if (previousUserId && previousUserId !== session.user.id) {
            try {
              localStorage.removeItem('activeShopId');
              localStorage.removeItem('chronicle_selected_context');
              localStorage.removeItem('joinTeamToken');
              localStorage.removeItem('startTutorialOnLoad');
              localStorage.removeItem('beta_invite_data');
            } catch {
              // localStorage may be unavailable; ignore.
            }
            try { queryClient.clear(); } catch { /* best-effort; ignore */ }
          }
          // Only flip the loading flag + queue `loadShopsOnce` when we're
          // actually about to (re)load shop data. `loadShopsOnce` short-
          // circuits when `lastLoadedUserIdRef.current === session.user.id`
          // (the same-user dedup case, hit on every TOKEN_REFRESHED /
          // USER_UPDATED after recovery), so flipping the flag here would
          // strand `isShopDataLoading=true` — ProtectedRoute then renders
          // the full-page "Loading..." until manual refresh. Audit:
          // docs/audits/stuck-loading-after-tab-return-audit.md.
          if (previousUserId !== session.user.id) {
            // Mark shop data as loading SYNCHRONOUSLY so isPassportMode
            // doesn't briefly evaluate true while loadUserShops is queued
            setIsShopDataLoading(true);
            // Defer shop loading
            setTimeout(() => {
              loadShopsOnce(session.user.id);
            }, 0);
          }
          // else: same user (TOKEN_REFRESHED / USER_UPDATED) — shop data
          // is already loaded and current; nothing to do.
        } else {
          lastLoadedUserIdRef.current = null;
          // FIX-R32-CRIT-1: clear every identity-linked localStorage key when
          // the session disappears. Previously this branch cleared React state
          // only, so a raw `supabase.auth.signOut()` (bypassing AuthContext.signOut)
          // left activeShopId / chronicle context / join-team token in storage.
          // Next sign-in would then silently restore the prior account's shop.
          setUserShops([]);
          setActiveShopState(null);
          setMembership(null);
          setIsShopDataLoading(false);
          // FIX-R38-CRIT-1: reset school membership count too on logout.
          setSchoolMembershipCount(0);
          try {
            localStorage.removeItem('activeShopId');
            localStorage.removeItem('chronicle_selected_context');
            localStorage.removeItem('joinTeamToken');
            localStorage.removeItem('startTutorialOnLoad');
            // FIX-R130-HIGH-1: see signOut() — same beta_invite_data risk
            // applies when sign-out happens via any path that bypasses
            // AuthContext.signOut and reaches us via onAuthStateChange.
            localStorage.removeItem('beta_invite_data');
          } catch {
            // localStorage may be unavailable in some embedded contexts; ignore.
          }
          // FIX-R130-CRIT-1: drop react-query cache for any signOut path,
          // not just AuthContext.signOut(). Mirrors the localStorage
          // cleanup above so a raw supabase.auth.signOut() from
          // anywhere in the app produces a fully-clean reset.
          try {
            queryClient.clear();
          } catch (err) {
            console.warn('[AUTH] queryClient.clear() failed (non-fatal):', err);
          }
          updateFeatureFlagsForShop(null);
        }

        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Same guard as the onAuthStateChange branch above — only flip
        // the loading flag when `loadShopsOnce` will actually run. On a
        // fresh AuthProvider mount `lastLoadedUserIdRef.current` is null
        // so this branch fires; on a re-mount where the ref happens to
        // match the session user (rare but possible), the dedup short-
        // circuit would otherwise strand `isShopDataLoading=true`.
        if (lastLoadedUserIdRef.current !== session.user.id) {
          setIsShopDataLoading(true);
          loadShopsOnce(session.user.id);
        }
      }

      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);


  // Check account_status on user load — auto-signout if deactivated
  useEffect(() => {
    if (!user?.id) return;
    
    const checkAccountStatus = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('account_status')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('[AUTH] Failed to check account_status:', error);
        return;
      }
      
      const status = data?.account_status ?? 'active';
      setAccountStatus(status);
      
      if (status === 'deactivated') {
        console.log('[AUTH] Account is deactivated, signing out');
        toast({
          title: 'Account Deactivated',
          description: 'Your account has been deactivated. Contact support if you believe this is an error.',
          variant: 'destructive',
        });
        await signOut();
      }
    };
    
    checkAccountStatus();
  }, [user?.id]);

  // FIX-R34-HIGH-3: realtime membership watcher for the current user. If a
  // manager revokes this user's membership, demotes them, or grants them a
  // new shop server-side, reload the shop list + membership so the UI
  // reflects the new access surface immediately instead of on next reload.
  // Without this, a demoted user could continue to see manager-level UI
  // (though RLS would block their writes) until they refresh.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`auth-memberships-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'memberships',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        console.log('[AUTH] membership change detected for current user — reloading shops');
        lastLoadedUserIdRef.current = null;
        loadUserShops(user.id);
      })
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: auth-memberships is a security-critical
        // stream — without a status callback, a silent TIMED_OUT leaves
        // the user holding stale shop access UI until a page reload
        // (RLS still blocks writes, but the demoted-user continues to
        // see privileged chrome). Logging the drop lets oncall diagnose.
        //
        // Batch 11 normalizer adoption: this was the last call site still
        // running the pre-normalizer block, which lumped CLOSED in with
        // CHANNEL_ERROR / TIMED_OUT and console.error'd all three. CLOSED
        // with no `err` is the signal supabase-js fires on every
        // `removeChannel(...)` — i.e. this effect's own cleanup, which runs
        // on each `user?.id` transition during sign-in. That produced a red
        // "memberships realtime status: CLOSED undefined" line on every
        // login that looked like a dropped security stream but was routine
        // teardown. `logRealtimeStatus` keeps CHANNEL_ERROR at error and
        // TIMED_OUT at warn, and demotes only the clean CLOSED to debug.
        logRealtimeStatus('[AuthContext memberships]', status, err);
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // ARC-A2: memoize the provider value so consumers see a stable
  // reference across renders that don't change any of the 14 fields.
  // Pre-A2 the value was a fresh inline object literal each render —
  // any AuthProvider re-render produced a new identity for the entire
  // context, fanning render churn out to 461 useAuth() call sites
  // across 205 files (some of which include the value in useEffect /
  // useMemo / useCallback deps and re-fire downstream work on every
  // identity change). The 4 callbacks are now useCallback'd above with
  // their minimal correct deps; combined with this useMemo, the value
  // identity now changes only when one of the 14 fields actually
  // changes.
  const value = useMemo(
    () => ({
      user,
      session,
      activeShop,
      membership: membershipWithRoles,
      userShops,
      isLoading,
      isShopDataLoading,
      workRolesResolved,
      isPassportMode,
      hasScopeAccess,
      hasSchoolMembership,
      setActiveShop,
      signOut,
      refetchMembership,
      refetchUserShops,
      refreshActiveShop,
    }),
    [
      user,
      session,
      activeShop,
      membershipWithRoles,
      userShops,
      isLoading,
      isShopDataLoading,
      workRolesResolved,
      isPassportMode,
      hasScopeAccess,
      hasSchoolMembership,
      setActiveShop,
      signOut,
      refetchMembership,
      refetchUserShops,
      refreshActiveShop,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
